import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link2, Mail, UserPlus, Users } from "lucide-react";
import { useToast } from "@core/hooks/use-toast";
import {
  listPollInvitations,
  sendInvitationReminders,
  isInReminderCooldown,
  type InvitationRow,
} from "@core/services/InvitationService";
import { AddInviteesDialog } from "@core/components/invitations/AddInviteesDialog";

interface EditAudienceCardProps {
  pollId: string;
  /** Réglages enregistrés du sondage (audienceMode, audienceGroupName). */
  settings?: { audienceMode?: string; audienceGroupName?: string } | null;
  invitationsHref?: string;
  onNavigate?: (href: string) => void;
}

/**
 * Bloc « AUDIENCE » de l'écran de MODIFICATION.
 *
 * À la création, l'audience se choisit dans une carte en haut du formulaire.
 * En modification, cette carte était absente et remplacée par un simple lien
 * « Inviter par e-mail » peu visible (retour Julien, 2026-09-16) : on affiche
 * donc la même carte, au même endroit, avec l'état réel du sondage et les
 * actions possibles une fois publié — ajouter des invités, relancer ceux qui
 * n'ont pas voté, voir la liste.
 *
 * Le mode ne se change pas ici : ajouter des invités à un sondage « lien » le
 * fait passer en « E-mails directs » (cf. AddInviteesDialog).
 */
export const EditAudienceCard: React.FC<EditAudienceCardProps> = ({
  pollId,
  settings,
  invitationsHref,
  onNavigate,
}) => {
  const { t } = useTranslation(["polls", "creator"]);
  const { toast } = useToast();
  const [rows, setRows] = useState<InvitationRow[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [sending, setSending] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setRows(await listPollInvitations(pollId));
      setLoaded(true);
    } catch {
      setRows([]);
      setLoaded(false);
    }
  }, [pollId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!loaded) return null;

  const active = rows.filter((r) => r.status !== "revoked");
  const voted = active.filter((r) => r.has_voted || r.status === "voted");
  const pending = active.filter((r) => !r.has_voted && r.status !== "voted");
  const remindable = pending.filter((r) => !isInReminderCooldown(r));
  const hasInvitees = active.length > 0;
  const groupName = settings?.audienceGroupName;

  const icon = !hasInvitees ? (
    <Link2 className="h-4 w-4 text-green-400" /> // color-exempt
  ) : groupName ? (
    <Users className="h-4 w-4 text-purple-400" /> // color-exempt
  ) : (
    <Mail className="h-4 w-4 text-blue-400" /> // color-exempt
  );

  const title = !hasInvitees
    ? t("creator:intent.audience_link_title")
    : groupName
      ? t("creator:intent.audience_group_selected_title", { name: groupName })
      : t("creator:intent.audience_email_title");

  const description = !hasInvitees
    ? t("polls:invitations.edit_audience.no_invitees")
    : t("polls:invitations.edit_audience.summary", {
        count: active.length,
        voted: voted.length,
      });

  const handleRemind = async () => {
    setSending(true);
    try {
      const res = await sendInvitationReminders(
        pollId,
        remindable.map((r) => r.id),
      );
      toast(
        res.successCount > 0
          ? {
              title: t("polls:invitations.tracking.action_resent_bulk", {
                count: res.successCount,
              }),
            }
          : {
              title: t("polls:invitations.error_title"),
              description: t(
                "polls:invitations.tracking.action_resent_bulk_error",
                { count: res.failedCount },
              ),
              variant: "destructive",
            },
      );
      await load();
    } finally {
      setSending(false);
    }
  };

  const actionClass =
    "rounded-lg px-3 py-1.5 text-xs font-medium whitespace-nowrap disabled:opacity-50";

  return (
    <div data-testid="edit-audience-card">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {t("creator:intent.cap_audience")}
        </span>
      </div>

      <div className="flex items-center gap-3 rounded-xl border border-border bg-muted px-3 py-2">
        <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-lg bg-accent">
          {icon}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-semibold text-foreground">
            {title}
          </span>
          <span
            className="block truncate text-[11.5px] text-muted-foreground"
            data-testid="edit-audience-summary"
          >
            {description}
          </span>
        </span>

        <div className="flex flex-shrink-0 items-center gap-1.5">
          {pending.length > 0 &&
            (remindable.length > 0 ? (
              <button
                type="button"
                onClick={handleRemind}
                disabled={sending}
                data-testid="edit-audience-remind"
                className={`${actionClass} border border-border bg-card text-foreground hover:bg-accent`}
              >
                {t("polls:invitations.tracking.pending_banner_remind", {
                  count: remindable.length,
                })}
              </button>
            ) : (
              <span className="text-[11.5px] text-muted-foreground">
                {t("polls:invitations.tracking.resend_all_cooldown")}
              </span>
            ))}
          {/* Icône seule (retour Julien : la carte prenait trop de place) ;
              libellé conservé en infobulle et pour les lecteurs d’écran. */}
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            data-testid="edit-audience-add"
            aria-label={t("polls:invitations.add.open_button")}
            title={t("polls:invitations.add.open_button")}
            className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <UserPlus className="h-4 w-4" />
          </button>
          {hasInvitees && invitationsHref && (
            <button
              type="button"
              onClick={() => onNavigate?.(invitationsHref)}
              data-testid="edit-audience-see-all"
              className={`${actionClass} text-muted-foreground hover:underline`}
            >
              {t("polls:invitations.tracking.pending_banner_see_all")}
            </button>
          )}
        </div>
      </div>

      <AddInviteesDialog
        pollId={pollId}
        open={addOpen}
        onOpenChange={setAddOpen}
        audienceMode={settings?.audienceMode}
        onAdded={() => void load()}
      />
    </div>
  );
};

export default EditAudienceCard;
